import { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from './config';
import { apiService } from '../services/api';
import SignupDetailsModal from '../components/SignupDetailsModal';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [backendUserId, setBackendUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({ title: '', message: '', redirectToSignUp: false });
  const [signupLoading, setSignupLoading] = useState(false);
  const [pendingTestResults, setPendingTestResults] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      if (firebaseUser) {
        const storedId = localStorage.getItem('backend_user_id');
        if (storedId) setBackendUserId(storedId);
      }
    });

    return () => unsubscribe();
  }, []);

  // Show message modal
  const showMessage = (title, message, redirectToSignUp = false) => {
    setMessageModalContent({ title, message, redirectToSignUp });
    setShowMessageModal(true);
  };

  // Handle message modal close
  const handleMessageModalClose = () => {
    setShowMessageModal(false);
    if (messageModalContent.redirectToSignUp) {
      // Redirect to sign up flow
      setTimeout(() => {
        signUpWithDetails(pendingTestResults);
      }, 300);
    }
    setMessageModalContent({ title: '', message: '', redirectToSignUp: false });
  };

  // SIGN IN DIRECTLY - No modal, just authenticate
  const signInDirect = async (testResults = null) => {
    setPendingTestResults(testResults);
    
    try {
      console.log('🔐 Starting Direct Sign-In...');
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      console.log('✅ Google sign-in success:', firebaseUser.email);

      // Try to login with backend
      try {
        console.log('🔍 Checking if user exists in backend...');
        const loginResponse = await apiService.login(firebaseUser.email);

        // User exists - login successful
        console.log('✅ Existing user found! Logging in...');
        
        if (loginResponse?.user?.user_id) {
          const backendId = loginResponse.user.user_id;
          localStorage.setItem('backend_user_id', backendId);
          setBackendUserId(backendId);

          if (loginResponse.user.stats) {
            localStorage.setItem('user_stats', JSON.stringify(loginResponse.user.stats));
          }

          console.log('✅ Login successful! Backend user ID:', backendId);
        }

        setPendingTestResults(null);
        return { success: true };

      } catch (loginError) {
        // User doesn't exist - show message
        console.log('❌ User not found in backend');
        await signOut(auth); // Clean up Firebase auth
        
        showMessage(
          'Account Not Found',
          'We couldn\'t find an account with this email. Please sign up first to create your account.',
          true // Redirect to sign up
        );

        return { success: false, error: 'User not found' };
      }

    } catch (error) {
      console.error('❌ Google sign-in error:', error);
      
      if (error?.code === 'auth/popup-closed-by-user') {
        // User closed popup - just return
        return { success: false, error: 'Popup closed' };
      } else if (error?.code === 'auth/unauthorized-domain') {
        alert('Unauthorized domain. Add this domain in Firebase Console → Authentication → Authorized domains.');
      } else {
        alert('Sign-in failed: ' + (error.message || 'Unknown error'));
      }
      
      return { success: false, error: error.message };
    }
  };

  // SIGN UP WITH DETAILS - Show modal first, then authenticate
  const signUpWithDetails = async (testResults = null) => {
    console.log('📝 Opening signup details modal...');
    setPendingTestResults(testResults);
    setShowSignupModal(true);
    return { success: true, pendingAuth: true };
  };

  // Handle when user submits the signup details modal
  const handleDetailsSubmit = async (userDetails) => {
    setSignupLoading(true);

    try {
      // Step 1: Google Sign-In
      console.log('🔐 Starting Google Sign-In for signup...');
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      
      console.log('✅ Google sign-in success:', firebaseUser.email);

      // Step 2: Check if user already exists
      try {
        console.log('🔍 Checking if user already exists...');
        const loginResponse = await apiService.login(firebaseUser.email);

        // User already exists - auto login them
        console.log('✅ User already exists! Auto-logging in...');
        
        if (loginResponse?.user?.user_id) {
          const backendId = loginResponse.user.user_id;
          localStorage.setItem('backend_user_id', backendId);
          setBackendUserId(backendId);

          if (loginResponse.user.stats) {
            localStorage.setItem('user_stats', JSON.stringify(loginResponse.user.stats));
          }

          console.log('✅ Login successful! Backend user ID:', backendId);
        }

        // Close modal and show message
        setShowSignupModal(false);
        setSignupLoading(false);
        setPendingTestResults(null);

        showMessage(
          'Account Already Exists',
          'You already have an account with us. We\'ve signed you in automatically!',
          false
        );

        return { success: true };

      } catch (loginError) {
        // User doesn't exist - create new account
        console.log('🆕 New user! Creating account...');

        const signupPayload = {
          email: firebaseUser.email,
          full_name: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          ...userDetails,
          unique_user_name: firebaseUser.uid,
          test_result: pendingTestResults || null
        };

        console.log('📦 Signup payload:', signupPayload);

        try {
          const signupResponse = await apiService.signup(signupPayload);

          if (signupResponse?.user?.user_id) {
            const backendId = signupResponse.user.user_id;
            localStorage.setItem('backend_user_id', backendId);
            setBackendUserId(backendId);

            if (signupResponse.user.stats) {
              localStorage.setItem('user_stats', JSON.stringify(signupResponse.user.stats));
            }

            console.log('✅ Signup successful! Backend user ID:', backendId);
          }

          // Close modal
          setShowSignupModal(false);
          setPendingTestResults(null);
          setSignupLoading(false);

          return { success: true };

        } catch (signupError) {
          console.error('❌ Backend signup failed:', signupError);
          await signOut(auth); // Clean up Firebase auth
          setSignupLoading(false);
          setShowSignupModal(false);
          setPendingTestResults(null);
          alert('Failed to create account. Please try again.');
          return { success: false, error: signupError.message };
        }
      }

    } catch (error) {
      console.error('❌ Google sign-in error:', error);
      setSignupLoading(false);
      
      if (error?.code === 'auth/popup-closed-by-user') {
        // User closed popup - just close modal
        setShowSignupModal(false);
        setPendingTestResults(null);
      } else if (error?.code === 'auth/unauthorized-domain') {
        alert('Unauthorized domain. Add this domain in Firebase Console → Authentication → Authorized domains.');
      } else {
        alert('Sign-in failed: ' + (error.message || 'Unknown error'));
      }
      
      return { success: false, error: error.message };
    }
  };

  // Handle modal close
  const handleModalCancel = () => {
    setShowSignupModal(false);
    setPendingTestResults(null);
    setSignupLoading(false);
  };

  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('backend_user_id');
      localStorage.removeItem('user_stats');
      setBackendUserId(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    backendUserId,
    loading,
    signInDirect,
    signUpWithDetails,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Signup Details Modal */}
      <SignupDetailsModal
        isOpen={showSignupModal}
        onClose={handleModalCancel}
        onSubmit={handleDetailsSubmit}
        loading={signupLoading}
      />

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">{messageModalContent.title}</h2>
            </div>
            <div className="p-6">
              <p className="text-base text-gray-700 mb-6">{messageModalContent.message}</p>
              <button
                onClick={handleMessageModalClose}
                className="w-full px-4 py-3 text-white bg-primary rounded-lg font-medium hover:bg-primary-dark transition-colors shadow-md"
              >
                {messageModalContent.redirectToSignUp ? 'Go to Sign Up' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export default AuthContext;